import React, { useCallback, useEffect, useState } from 'react';

import VideoPlayer from '@rover/components/src/video/VideoPlayer';
import KEYS from '@rover/constants/src/platform/keys.constants';
import { Box, Flex } from '@rover/kibble/core';
import LoadingShimmer from '@rover/kibble/patterns/LoadingShimmer';
import { isReactNative } from '@rover/rsdk/src/modules/Network/userAgent';

import {
  DIMMED_IMAGE_OPACITY,
  MAX_LIGHTBOX_IMAGE_HEIGHT,
  MAX_LIGHTBOX_IMAGE_WIDTH,
} from './constants';
import { isDimmedImage, isGalleryVideoItem } from './ImageGallery.common';
import ImageWithShimmer from './ImageWithShimmer';
import type { LightboxPagerProps } from './LightboxPager.common';
import LightboxPagerControls from './LightboxPagerControls';
import { shouldLoadMore } from './utils';

const LightboxPager = ({
  images,
  lightboxIndex,
  setLightboxIndex,
  pagination,
  gallery,
  dimmedImageIds,
}: LightboxPagerProps): JSX.Element => {
  const {
    currentUri,
    currentSize,
    isCurrentImageFirst,
    isCurrentImageLast,
    hasNextImage,
    hasPrevImage,
    currentIndex,
  } = gallery;

  const [cachedImages, setCachedImages] = useState<{ [k: string]: boolean }>({});

  const showPrev = useCallback((): void => {
    if (isCurrentImageFirst) return;
    setLightboxIndex((prevIndex) => prevIndex - 1);
  }, [isCurrentImageFirst, setLightboxIndex]);

  const showNext = useCallback((): void => {
    if (isCurrentImageLast) return;
    if (shouldLoadMore(pagination) && lightboxIndex >= images.length - 2) {
      pagination?.onLoadMore();
    }
    setLightboxIndex((prevIndex) => prevIndex + 1);
  }, [isCurrentImageLast, setLightboxIndex, pagination, lightboxIndex, images]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      // This listener is on `document`, so it hears arrows pressed anywhere —
      // including inside the video player, where they belong to the transport
      // controls and should scrub rather than change gallery item.
      const target = event.target as HTMLElement | null;
      if (target?.closest?.('video')) return;

      if (event.key === KEYS.LEFT) {
        showPrev();
      } else if (event.key === KEYS.RIGHT) {
        showNext();
      }
    },
    [showPrev, showNext]
  );

  useEffect(() => {
    if (process.env.JS_ENV_CLIENT) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
    return () => {};
  }, [handleKeyDown]);

  const isWaitingForImages = lightboxIndex >= images.length;
  const currentImage = images[currentIndex];
  const currentVideo = currentImage && isGalleryVideoItem(currentImage) ? currentImage : undefined;

  const renderCurrentItem = (): JSX.Element => {
    // Only the full view plays; the "view all" grid shows the thumbnail with a
    // play badge. Navigating away unmounts the player, whose own cleanup stops
    // playback.
    if (currentVideo) {
      return (
        <Box
          width="100%"
          maxWidth={MAX_LIGHTBOX_IMAGE_WIDTH}
          height={isReactNative() ? MAX_LIGHTBOX_IMAGE_HEIGHT : undefined}
        >
          {/* `posterUrl` is web-only: expo-video shows the video's own first
              frame instead, so the tile-to-player transition is continuous on
              web and a jump to a possibly unrelated frame on native. Accepted
              rather than wired, so don't go looking for the native half. */}
          <VideoPlayer
            sources={currentVideo.video.sources}
            description={currentVideo.video.description}
            posterUrl={currentVideo.largeUncropped || undefined}
          />
        </Box>
      );
    }

    if (isWaitingForImages) {
      return (
        <LoadingShimmer
          width={MAX_LIGHTBOX_IMAGE_WIDTH}
          height={isReactNative() ? MAX_LIGHTBOX_IMAGE_HEIGHT : MAX_LIGHTBOX_IMAGE_WIDTH}
          borderRadius="primary"
          m="0x"
          data-testid="lightbox-loading-shimmer"
        />
      );
    }

    return (
      <ImageWithShimmer
        src={currentUri}
        caption={currentImage?.caption}
        altText={currentImage?.altText}
        width={currentSize.width || MAX_LIGHTBOX_IMAGE_WIDTH}
        height={
          isReactNative()
            ? MAX_LIGHTBOX_IMAGE_HEIGHT
            : currentSize.height || MAX_LIGHTBOX_IMAGE_WIDTH
        }
        cached={cachedImages[currentUri]}
        setCached={(cached: boolean) => {
          setCachedImages((prev) => ({ ...prev, [currentUri]: cached }));
        }}
      />
    );
  };

  const isCurrentImageDimmed = isDimmedImage(currentImage, dimmedImageIds);

  return (
    <Box position="relative" width="100%" textAlign="center">
      <LightboxPagerControls
        hasPrev={hasPrevImage}
        hasNext={hasNextImage}
        onPrev={showPrev}
        onNext={showNext}
      />
      <Flex
        width="100%"
        maxHeight="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Box
          testID="lightbox-page"
          sx={isCurrentImageDimmed ? { opacity: DIMMED_IMAGE_OPACITY } : undefined}
        >
          {renderCurrentItem()}
        </Box>
      </Flex>
    </Box>
  );
};

export default LightboxPager;
